import { useCallback } from 'react'
import { DockviewReact, themeAbyss, themeLight, type DockviewApi, type DockviewReadyEvent } from 'dockview'
import 'dockview/dist/styles/dockview.css'
import { PANELS, dockviewComponents } from './panels'
import { PanelTab } from './tab'
import { useSkin } from '../state/skin'

const LAYOUT_KEY = 'sb.layout.v3'

function buildDefaultLayout(api: DockviewApi): void {
  api.clear()
  api.addPanel({ id: 'extensions', component: 'extensions', title: 'Extensions' })
  api.addPanel({ id: 'mycalls', component: 'mycalls', title: 'My calls',
    position: { referencePanel: 'extensions', direction: 'right' } })
  api.addPanel({ id: 'queuedetail', component: 'queuedetail', title: 'Queue detail',
    position: { referencePanel: 'mycalls', direction: 'within' } })
  api.addPanel({ id: 'parked', component: 'parked', title: 'Parked',
    position: { referencePanel: 'mycalls', direction: 'right' } })
  api.addPanel({ id: 'queues', component: 'queues', title: 'Queues',
    position: { referencePanel: 'parked', direction: 'below' } })
  api.addPanel({ id: 'voicemail', component: 'voicemail', title: 'Voicemail',
    position: { referencePanel: 'queues', direction: 'below' } })
  api.addPanel({ id: 'contacts', component: 'contacts', title: 'Contacts',
    position: { referencePanel: 'extensions', direction: 'below' } })
  api.addPanel({ id: 'history', component: 'history', title: 'Call history',
    position: { referencePanel: 'contacts', direction: 'within' } })
  api.getPanel('contacts')?.api.setActive()
  api.getPanel('mycalls')?.api.setActive()
}

export function Workspace({ onApi }: { onApi?: (api: DockviewApi) => void }) {
  const skin = useSkin()
  const onReady = useCallback((event: DockviewReadyEvent) => {
    const api = event.api
    const saved = localStorage.getItem(LAYOUT_KEY)
    let restored = false
    if (saved) {
      try { api.fromJSON(JSON.parse(saved)); restored = true } catch { restored = false }
    }
    if (!restored) buildDefaultLayout(api)
    api.onDidLayoutChange(() => {
      try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(api.toJSON())) } catch { /* ignore */ }
    })
    onApi?.(api)
  }, [onApi])

  return (
    <DockviewReact
      className="sb-dock"
      theme={skin === 'daylight' ? themeLight : themeAbyss}
      components={dockviewComponents}
      defaultTabComponent={PanelTab}
      onReady={onReady}
    />
  )
}

export function openPanel(api: DockviewApi, id: string): void {
  const existing = api.getPanel(id)
  if (existing) { existing.api.setActive(); return }
  const def = PANELS.find((p) => p.id === id)
  if (def) api.addPanel({ id: def.id, component: def.id, title: def.title })
}

export function resetLayout(api: DockviewApi): void {
  localStorage.removeItem(LAYOUT_KEY)
  buildDefaultLayout(api)
}
